import { computed, watch, ref, type ComputedRef, reactive, toRefs } from 'vue';
import { useLocale } from '@/composables/locale';
import { paymentsApi } from '@/api/payments';
import {
    useGet,
    usePost,
    usePut,
    useToaster,
    useFilters,
} from '@foodics/ui-common';
import type {
    TransferCountByStatus,
    CancelPaymentRequest,
    RetryPaymentRequest,
    SendNowPaymentRequest,
    EditPaymentRequest,
    PaymentDetailsRequest,
    PaymentsRequest,
} from '@/types/api/payments';
import { useMoveMoney } from '@/composables/moveMoney';
import { format } from 'date-fns';
import { PaymentStatusEnum } from '@/enums/payments';
import type { PaymentStatus, Payment } from '@/types/models';
import { createSharedComposable, createEventHook } from '@vueuse/core';
import { useCurrentUser } from '../currentUser';
import { onUpdateApprovalDecision } from './paymentApprovals';
import { merge } from 'lodash-es';
import type { PaymentAction } from './paymentActions';
import { parseNumber } from '@/utils/number';
import { parseError } from '@/utils/errors';
import { usePagination } from '../usePagination';

const { t, onLocaleChange } = useLocale();

export const statusMap = computed(
    (): Record<PaymentStatus, string> => ({
        [PaymentStatusEnum.NEW]: t('payments.statusMap.NEW'),
        [PaymentStatusEnum.PENDING_APPROVAL]: t(
            'payments.statusMap.PENDING_APPROVAL',
        ),
        [PaymentStatusEnum.REQUESTED]: t('payments.statusMap.REQUESTED'),
        [PaymentStatusEnum.SCHEDULED]: t('payments.statusMap.SCHEDULED'),
        [PaymentStatusEnum.PROCESSED]: t('payments.statusMap.PROCESSED'),
        [PaymentStatusEnum.FAILED]: t('payments.statusMap.FAILED'),
        [PaymentStatusEnum.RETURNED]: t('payments.statusMap.RETURNED'),
        [PaymentStatusEnum.REJECTED]: t('payments.statusMap.REJECTED'),
        [PaymentStatusEnum.CANCELED]: t('payments.statusMap.CANCELED'),
        [PaymentStatusEnum.APPROVED]: t('payments.statusMap.APPROVED'),
        [PaymentStatusEnum.RETRIED]: t('payments.statusMap.RETRIED'),
    }),
);

export const processedStatuses: PaymentStatus[] = [PaymentStatusEnum.PROCESSED];

export const rejectedCancelledStatuses: PaymentStatus[] = [
    PaymentStatusEnum.REJECTED,
    PaymentStatusEnum.CANCELED,
];

export const actionRequiredStatuses: PaymentStatus[] = [
    PaymentStatusEnum.RETURNED,
    PaymentStatusEnum.FAILED,
];

export const processingStatuses: PaymentStatus[] = [
    PaymentStatusEnum.REQUESTED,
    PaymentStatusEnum.NEW,
    PaymentStatusEnum.APPROVED,
];

export const actionRequiredStatusesFilter = computed(() => {
    return [
        {
            label: t('payments.action_required_filters.all'),
            value: actionRequiredStatuses,
        },
        {
            label: t('payments.action_required_filters.returned'),
            value: [PaymentStatusEnum.RETURNED],
        },
        {
            label: t('payments.action_required_filters.failed'),
            value: [PaymentStatusEnum.FAILED],
        },
    ];
});
export type TabKey =
    | 'processed'
    | 'action-required'
    | 'pending-approval'
    | 'scheduled'
    | 'processing'
    | 'rejected-cancelled';
export type Tab = {
    key: TabKey;
    label: string;
    count?: ComputedRef<number>;
    statuses: (typeof PaymentStatusEnum)[keyof typeof PaymentStatusEnum][];
    isDrawerView: boolean;
};
const tabsConfig = computed<Tab[]>(() => {
    return [
        {
            key: 'processed',
            label: t('payments.tabs.processed'),
            statuses: processedStatuses,
            isDrawerView: true,
        },
        {
            key: 'action-required',
            label: t('payments.tabs.action_required'),
            statuses: actionRequiredStatuses,
            isDrawerView: false,
        },
        {
            key: 'pending-approval',
            label: t('payments.tabs.pending_approval'),
            statuses: [PaymentStatusEnum.PENDING_APPROVAL],
            isDrawerView: false,
        },
        {
            key: 'scheduled',
            label: t('payments.tabs.scheduled'),
            statuses: [PaymentStatusEnum.SCHEDULED],
            isDrawerView: true,
        },
        {
            key: 'processing',
            label: t('payments.tabs.processing'),
            statuses: processingStatuses,
            isDrawerView: true,
        },
        {
            key: 'rejected-cancelled',
            label: t('payments.tabs.rejected_cancelled'),
            statuses: rejectedCancelledStatuses,
            isDrawerView: true,
        },
    ];
});

export const failureReasonsMap = computed<
    Record<Payment['status_message'], string>
>(() => {
    return {
        insufficient_funds: t('payments.failure_reasons.insufficient_funds'),
        debtor_account_identification_inactive: t(
            'payments.failure_reasons.debtor_account_identification_inactive',
        ),
        duplicate_sender_reference: t(
            'payments.failure_reasons.duplicate_sender_reference',
        ),
        debtor_account_identification_mismatch: t(
            'payments.failure_reasons.debtor_account_identification_mismatch',
        ),
    };
});

export const errorTitleMap = computed<
    Record<Payment['status_message'], string>
>(() => {
    return {
        insufficient_funds: t('payments.error_titles.insufficient_funds'),
        debtor_account_identification_inactive: t(
            'payments.error_titles.debtor_account_identification_inactive',
        ),
        duplicate_sender_reference: t(
            'payments.error_titles.duplicate_sender_reference',
        ),
        debtor_account_identification_mismatch: t(
            'payments.error_titles.debtor_account_identification_mismatch',
        ),
    };
});

export const getErrorObject = (error: Payment['status_message']) => {
    const failureReasons = failureReasonsMap.value;
    const errorTitles = errorTitleMap.value;
    return {
        title:
            errorTitles[error] ??
            t(
                'generic.common_terms.something_went_wrong',
                'Something went wrong',
            ),
        subtext:
            failureReasons[error] ??
            t(
                'payments.failure_reasons.default',
                'Something went wrong while processing this payment. Please try again later.',
            ),
    };
};
const { onMoveMoneyRequestFulfilled } = useMoveMoney();
const { user: currentUser } = useCurrentUser();
export const { setFilter, appliedFilters } = useFilters<PaymentFilters>();
export const { trigger: triggerPaymentUpdate, on: onPaymentUpdateFulfilled } =
    createEventHook<
        [Partial<Payment> & { status: PaymentStatus }, PaymentAction]
    >();
export type PaymentFilters = {
    status?: string;
};
export const paymentsFetched = ref(false);
export const usePayments = createSharedComposable(() => {
    const payments = ref<Payment[]>([]);

    const apiParams = computed(() => {
        return {
            ...(appliedFilters.value && {
                filter: appliedFilters.value,
            }),
            include: 'destination.bank',
        };
    });

    const {
        items,
        isLoading,
        hasNextPage,
        fetchNextPage,
        hasFetchedInitialPage,
        resetPagination,
    } = usePagination<PaymentsRequest>(paymentsApi.list, {
        fetchOptions: computed(() => ({ params: apiParams.value })),
    });
    watch(hasFetchedInitialPage, (newValue) => {
        if (newValue) {
            paymentsFetched.value = true;
        }
    });

    onUpdateApprovalDecision((updatedData) => {
        [...payments.value].forEach((payment) => {
            const approval = payment.approvals.find(
                (approval) => approval.id === updatedData.approvalId,
            );
            if (!approval) return;

            approval.decision = updatedData.decision;
        });
    });

    watch(
        appliedFilters,
        () => {
            payments.value = [];
            resetPagination();
        },
        { deep: true },
    );

    watch(items, () => {
        payments.value.push(...items.value);
    });

    function find(id: string): Payment | undefined {
        return payments.value.find((payment) => payment.id === id);
    }

    onPaymentUpdateFulfilled(([updatedPayment, action]) => {
        if (
            action === 'cancel' ||
            updatedPayment.status === PaymentStatusEnum.REQUESTED ||
            updatedPayment.status === PaymentStatusEnum.PENDING_APPROVAL
        ) {
            payments.value = payments.value.filter(
                (payment) => payment.id !== updatedPayment.id,
            );
        } else {
            const paymentToUpdateIdx = payments.value.findIndex(
                (payment) => payment.id === updatedPayment.id,
            );
            if (paymentToUpdateIdx === -1) return;
            payments.value[paymentToUpdateIdx] = merge(
                payments.value[paymentToUpdateIdx],
                updatedPayment,
            );
        }
    });
    onMoveMoneyRequestFulfilled(() => {
        payments.value = [];
        resetPagination();
    });

    const context = {
        fetchNextPage,
        hasNextPage,
        hasFetchedInitialPage,
        setFilter,
        appliedFilters,
        isLoading,
        payments,
        find,
    };
    return context;
});
export const usePaymentDetails = (paymentId: string) => {
    const { data, isLoading, onData, execute } = useGet<
        PaymentDetailsRequest['response']
    >(paymentsApi.details(paymentId));

    return {
        payment: computed(() => data.value?.data),
        isLoading,
        onData,
        execute,
    };
};

export const useCancelPayment = (payment: Payment) => {
    const { addToast } = useToaster();
    const { execute, error, isLoading, onData } = usePost<
        CancelPaymentRequest['payload'],
        CancelPaymentRequest['response']
    >(paymentsApi.cancel(payment.id));

    const cancelPayment = async (payload: CancelPaymentRequest['payload']) => {
        const { t } = useLocale();
        try {
            await execute(payload);
            if (error.value) throw error.value;
            triggerPaymentUpdate([payment, 'cancel']);
            addToast({
                title: t(
                    'payments.modals.cancel_payment.success_payment_message',
                ),
                variant: 'success',
            });
        } catch (error) {
            addToast({
                title: parseError(error),
                variant: 'error',
            });
        }
    };
    return {
        cancelPayment,
        isLoading,
        onCancelPaymentFulfilled: onData,
    };
};
export const useRetryPayment = (paymentId: string) => {
    const { addToast } = useToaster();
    const { execute, error, isLoading, onData } = usePost<
        RetryPaymentRequest['payload'],
        RetryPaymentRequest['response']
    >(paymentsApi.retry(paymentId));

    const retryPayment = async (otp_token: string) => {
        const { t } = useLocale();
        try {
            await execute({ otp_token });
            if (error.value) throw error.value;
            triggerPaymentUpdate([
                { id: paymentId, status: PaymentStatusEnum.REQUESTED },
                'retry',
            ]);
            addToast({
                title: t('payments.retry.payment_processing'),
                variant: 'success',
            });
        } catch (error) {
            addToast({
                title: parseError(error),
                variant: 'error',
            });
        }
    };
    return {
        retryPayment,
        isLoading,
        onRetryPaymentFulfilled: onData,
    };
};
export const useEditPayment = createSharedComposable((payment: Payment) => {
    const { addToast } = useToaster();
    const { t } = useLocale();
    const editValues = reactive({
        amount: payment.amount.value.toString(),
        submit_at: format(payment.submit_at, 'yyyy-MM-dd'),
    });
    const { execute, error, isLoading } = usePut<
        EditPaymentRequest['payload'],
        EditPaymentRequest['response']
    >(paymentsApi.edit(payment.id));

    const editPayment = async (hasApprovals: boolean, otp_token: string) => {
        try {
            await execute({
                amount: parseNumber(editValues.amount),
                submit_at: editValues.submit_at,
                otp_token,
            });
            if (error.value) throw error.value;
            addToast({
                title: hasApprovals
                    ? t('payments.payment_edit.payment_sent_for_approval')
                    : t('payments.payment_edit.scheduled_payment_updated'),
                variant: 'success',
            });

            triggerPaymentUpdate([
                {
                    id: payment.id,
                    amount: {
                        ...payment.amount,
                        value: Number(editValues.amount),
                    },
                    submit_at: editValues.submit_at,
                    status: PaymentStatusEnum.SCHEDULED,
                },
                'edit',
            ]);
            if (hasApprovals) {
                triggerPaymentUpdate([
                    {
                        id: payment.id,
                        status: PaymentStatusEnum.PENDING_APPROVAL,
                    },
                    'edit',
                ]);
            }
        } catch (error) {
            addToast({
                title: parseError(error),
                variant: 'error',
            });
        }
    };

    return {
        ...toRefs(editValues),
        editPayment,
        isLoading,
    };
});

export const useSendNowPayment = (paymentId: string) => {
    const { addToast } = useToaster();
    const { execute, error, isLoading, onData } = usePost<
        SendNowPaymentRequest['payload'],
        SendNowPaymentRequest['response']
    >(paymentsApi.sendNow(paymentId));

    const sendNowPayment = async (otp_token: string) => {
        try {
            await execute({ otp_token });
            if (error.value) throw error.value;
            addToast({
                title: t('payments.retry.payment_processing'),
                variant: 'success',
            });
            triggerPaymentUpdate([
                { id: paymentId, status: PaymentStatusEnum.REQUESTED },
                'sendNow',
            ]);
        } catch (error) {
            addToast({
                title: parseError(error),
                variant: 'error',
            });
        }
    };
    return {
        sendNowPayment,
        isLoading,
        onSendNowPaymentFulfilled: onData,
    };
};

export const usePaymentTabs = () => {
    const tabs = ref<Tab[]>([]);
    const { data, isLoading, onData, execute } = useGet<
        TransferCountByStatus['response']
    >(paymentsApi.countByStatus);

    onData(() => {
        if (!data.value) return;

        if (tabs.value.length) {
            // Only update tabs whose counts have changed
            tabs.value.forEach((tab) => {
                const newCount = tab.statuses?.reduce((total, status) => {
                    return total + (data.value?.[status] ?? 0);
                }, 0);

                // Only update if the count actually changed
                if (tab.count !== newCount) {
                    tab.count = newCount;
                }
            });
        } else {
            tabs.value = tabsConfig.value.map((tab: Tab) => {
                const count = computed(() =>
                    tab.statuses?.reduce((total, status) => {
                        return total + (data.value?.[status] ?? 0);
                    }, 0),
                );

                return {
                    ...tab,
                    count: count.value,
                };
            });
        }
    });

    onPaymentUpdateFulfilled(() => {
        execute();
    });
    onMoveMoneyRequestFulfilled(() => {
        execute();
    });

    const tabsFiltered = computed(() =>
        tabs.value.filter(
            (tab) => !(tab.count === 0 && tab.key === 'action-required'),
        ),
    );

    onLocaleChange(() => {
        // when changing the language, the tabs Ref doesn't get updated, the the labels remain in the previous language (the one that has been intially set)
        // so we only make sure that the tabs Ref labels' are in sync with tabsConfig labels.

        tabs.value.forEach((tab, index) => {
            tab.label = tabsConfig.value[index].label;
        });
    });

    return {
        tabs: tabsFiltered,
        isLoading,
        execute,
    };
};

export const checkActionRequiredPayment = (status: PaymentStatus): boolean => {
    return actionRequiredStatuses.includes(status);
};

export const checkIfUserIsCreator = (creatorId: string) => {
    return creatorId === currentUser.value?.id;
};

export function getPaymentCreator(payment: Payment) {
    return payment.original_transfer?.creator || payment.creator;
}

export function resolvePaymentPartyName(payment: Payment) {
    return payment.destination_type === 'transfer'
        ? payment.destination.account_title
        : payment.beneficiary_name;
}

export * from './paymentActions';
export * from './paymentApprovals';
